/**
 * TRFer copy pack (#1731, spec §7.2) — staff talking to the caseload copilot,
 * William (#3066). Plain and factual; the copilot is named where the
 * companion pack says "I", because staff are addressing a tool, not a
 * companion.
 */
import i18n from 'i18next';
import type { ChatCopyPack } from '@core/config/types';
import { bcp47For } from '@/i18n/negotiate';
import { asPackT, drawerChrome, exportChrome, operationalCopy, untitledFallback } from './operational';
import { COPILOT_NAME } from '@/lib/companion-config';

const NAME = COPILOT_NAME;
const endsWithPunctuation = (s: string) => /[.!?]$/.test(s);

/**
 * #2109 R3-B24: the staff pack carries every slot the companion pack does,
 * with English values — staff surfaces stay English until R4. The operational
 * slots read the en catalog through a fixed-English `t`, whatever language
 * the reader chose, so the two packs cannot drift on shared wording.
 */
export function trferCopy(): ChatCopyPack {
  const t = asPackT(i18n.getFixedT('en'));
  const locale = bcp47For('en');
  return {
    ...operationalCopy(t, locale, 'en', `Ask ${NAME} about a family, a task or your caseload.`),
    bubble: {
      userLabel: 'You',
      senderNames: { stevens: NAME, assistant: NAME },
      senderFallback: NAME,
    },
    thinking: {
      fallbackCoachName: NAME,
      initialMessage: 'Reading your message',
      progressive: [
        { atMs: 5000, text: 'Checking the caseload' },
        { atMs: 10000, text: 'Pulling the relevant records' },
        { atMs: 15000, text: 'Putting a reply together' },
      ],
    },
    failures: {
      streamTimeout: {
        summary: `${NAME} took too long to finish this reply.`,
        bubbleAfterPartial: `⚠️ ${NAME} stopped part-way through this reply — it ran past the time limit. Everything above did come through. Send your message again to pick it back up.`,
        bubbleNoContent: `⚠️ ${NAME} took too long to reply and the request timed out. Try sending that again.`,
      },
      refusedMessageTooLong: {
        summary: 'This message is too long to send in one go.',
        bubble:
          '⚠️ That message is too long to send in one go. Your text is still shown above — copy it and send it in a couple of smaller parts.',
      },
      refused: {
        summary: (status) => `${NAME} refused the message (HTTP ${status}).`,
        bubble: (reason) =>
          `⚠️ Your message didn’t reach ${NAME} — ${reason}${endsWithPunctuation(reason) ? '' : '.'} Try again; if it keeps happening, reload the page and check you’re still signed in.`,
      },
      notOpened: {
        summary: `The connection to ${NAME} never opened.`,
        bubble: `⚠️ Your message didn’t reach ${NAME} — the connection never opened. That is usually a network drop. Try again; if it keeps happening, reload the page and check you’re still signed in.`,
      },
      interrupted: {
        summary: `The connection dropped part-way through ${NAME}’s reply.`,
        bubbleAfterPartial:
          '⚠️ The connection dropped part-way through this reply — what you see above is all that arrived. Send your message again to get the rest.',
        bubbleAtStart: `⚠️ The connection to ${NAME} dropped just as the reply was starting. Try sending that again.`,
      },
      sendRejected: {
        summary: (status) => `${NAME} rejected the message (HTTP ${status}).`,
        summaryNoStatus: `The message could not be sent to ${NAME}.`,
        bubble: (status) => `⚠️ ${NAME} couldn’t take that message — the server answered ${status}. Try again in a moment.`,
        bubbleNoStatus: `⚠️ That message couldn’t be sent to ${NAME}. Try again in a moment.`,
      },
      agentError: {
        summaryFallback: `${NAME} could not complete this reply.`,
        bubble: (reason) => `⚠️ ${NAME} couldn’t finish this reply — ${reason}`,
        bubbleNoReason: `⚠️ ${NAME} couldn’t finish this reply. Try again in a moment.`,
      },
      connectFailed: {
        summaryFallback: `The message never reached ${NAME}.`,
        bubble: (reason) => `⚠️ Your message never reached ${NAME} — ${reason}`,
        bubbleNoReason: `⚠️ Your message never reached ${NAME}. Try sending it again in a moment.`,
      },
      retryNotPersisted: {
        conflict: {
          summary: 'The regenerated reply could not be saved.',
          bubble:
            '⚠️ This answer was regenerated, but the conversation had already moved on, so the earlier reply above is the one on record.',
        },
        transient: {
          summary: 'The regenerated reply could not be saved.',
          bubble:
            '⚠️ This answer was regenerated, but saving it couldn’t be confirmed, so the earlier reply has been put back. Try again in a moment, or reload to see what was stored.',
        },
      },
    },
    upload: {
      unreachable: `That file didn’t reach ${NAME} — the connection dropped on the way. Check you’re online and try attaching it again.`,
      failedStatus: (status) => t('chat:core.upload.failedStatus', { status }),
    },
    export: {
      userLabel: 'You',
      textHeaderTitle: 'Conversation',
      textHeaderSubtitle: `A conversation with ${NAME}`,
      markdownTitle: 'Conversation export',
      markdownVersionLabel: `Conversation with ${NAME}`,
      printPdf: 'Print / PDF',
      preparing: 'Preparing…',
      spokenSuffix: ' (spoken)',
      printScopeNote:
        'This is the conversation as it stands now. If an answer was regenerated, only its current version appears here.',
      ...exportChrome(t),
    },
    drawer: {
      title: 'Conversations',
      groups: { today: 'Today', thisWeek: 'This week', earlier: 'Earlier' },
      channelBadges: {
        telephone: 'Call',
        'web-voice': 'Voice',
        voice: 'Voice',
      },
      untitledFallback: untitledFallback(t, locale),
      ...drawerChrome(t),
    },
    actions: {
      copyMessage: 'Copy message',
      copied: 'Copied',
      retryMessage: 'Regenerate reply',
    },
    recall: {
      line: () => 'Drawing on earlier notes',
      showDetails: 'What was recalled',
      hideDetails: 'Hide',
    },
    chips: {
      regionLabel: 'Suggested replies',
    },
    voice: {
      regionLabel: 'Voice call',
      states: {
        requesting: 'Asking for your microphone…',
        connecting: 'Connecting…',
        connectingSlow: 'Still connecting.',
        listening: 'Listening',
        speaking: `${NAME} is speaking`,
        muted: 'Muted',
        ended: (durationMs) => {
          const seconds = Math.max(1, Math.round(durationMs / 1000));
          return seconds < 60 ? `Ended · ${seconds} sec` : `Ended · ${Math.round(seconds / 60)} min`;
        },
      },
      failures: {
        micDenied: 'Microphone access was blocked. Allow it and try again — or keep typing.',
        micMissing: 'No microphone was found on this device. Keep typing instead.',
        agentAbsent: `${NAME} didn’t pick up. Try again in a moment — or keep typing.`,
        unavailable: 'Voice isn’t available right now. Keep typing instead.',
        network: 'The connection dropped. Try again — or keep typing.',
      },
      controls: {
        mute: 'Mute microphone',
        unmute: 'Unmute microphone',
        end: 'End call',
        minimize: 'Minimize call',
        expand: 'Expand call',
        retry: 'Try again',
      },
      duplexUnavailable:
        'Typing isn’t reaching this call — your message will wait until the call ends.',
    },
    cardCopy: {
      calibration: {
        title: 'Review entry',
        ariaLabel: 'Entry for review',
        approve: 'Approve',
        hold: 'Hold',
        delete: 'Delete',
        edit: 'Edit',
        clickToEdit: 'Click to edit',
      },
    },
  };
}
